import { Injectable } from '@angular/core';
import { GatewayResponse,GetCommissionsRequest } from '@dave/types';
import { Actions,createEffect,ofType } from '@ngrx/effects';
import { Action,Store } from '@ngrx/store';
import moment from 'moment';
import { ToastrService } from 'ngx-toastr';
import { EMPTY,exhaustMap,from,mergeMap,of } from 'rxjs';
import { catchError,concatMap,map,tap,withLatestFrom } from 'rxjs/operators';
import { isNotNullOrUndefined,stringifyIfNotNullOrUndefined,stringifyIfNotUndefined } from '../../../helper/helper';
import { CommissionEntityFromBackend,CommissionEntityFromGRPCBackend, CommissionEntitygqlFields } from '../../entities/commission.entity';
import { UserToCommissionEntity } from '../../entities/user-to-commission.entity';
import { AppGatewayService } from '../../services/app-gateway.service';
import { HttpService } from '../../services/http.service';
import { DaveActions } from '../actions/actions';
import { BaseActionTypes } from '../actions/base.actions';
import { CommissionActionTypes } from '../actions/commission.actions';
import { DamageFlowActionTypes } from '../actions/damageflow.actions';
import { UserToCommissionActionTypes } from '../actions/user-to-commission.action';
import { UserToCommissionShiftActionTypes } from '../actions/user2CommissionShift.action';
import { State } from '../index';
import { getToken, getTokenPayload } from '../selectors/base.selectors';
import { getCommissionById,getCommissions } from '../selectors/commission.selector';
import { getPartnerMainOffice } from '../selectors/partners.selectors';
import { getStatusFromBackoffice } from '../selectors/statusFromBackoffice.selectors';
import { getUserToCommissionFetched,getUserToCommissionLatestUpdatedAt } from '../selectors/user-to-commission.selector';
import { getUserToCommissionShiftFetched } from '../selectors/users-to-commission-shift.selectors';
import { getUser } from '../selectors/users.selectors';
import { GetTimestampFromTime } from '@dave/types/dist/convert';
import {
    getMySharepointToken
} from '../../../dave-file-explorer/components/file-explorer/sharepoint/sharepoint-helper';
import { CommissionResolver } from '../../guards/commission.resolver';

enum ErrorCodes {
    Add = 'Aufträge Hinzufügen fehlgeschlagen',
    Load = 'Aufträge Abrufen fehlgeschlagen',
    Modify = 'Aufträge Bearbeiten fehlgeschlagen',
    Remove = 'Aufträge Löschen fehlgeschlagen',
    SetUser = 'Nutzer Setzen fehlgeschlagen',
    AddRessources = 'Ressourcen hinzufügen fehlgeschlagen',
}
const KAPL_PARTNERID: number = window["env"]["kapl_partner_id"];

@Injectable()
export class CommissionEffects {
    AddResourceCommission$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CommissionActionTypes.AddResources),
            withLatestFrom(this.store$.select(getToken)),
            concatMap(([{ Payload }, token]) => {
                const queryString = `
mutation{
  addResourcesFromLedgerImportId(commissionId: ${Payload.commissionId}, ledgerImportId: ${Payload.ledgerImportId}){id}
}`;
                return this.gatewayHttpService.graphQl({ query: queryString }, { token }).pipe(
                    map((res) => {
                        if (res?.addResourcesFromLedgerImportId) {
                            return CommissionActionTypes.AddResourcesSuccess();
                        }
                        return BaseActionTypes.ErrorAction({ Payload: { ToasterMessage: ErrorCodes.AddRessources } });
                    }),
                    catchError((err, caught) =>
                        of(
                            BaseActionTypes.ErrorAction({
                                Payload: {
                                    ToasterMessage: ErrorCodes.AddRessources,
                                    Err: err,
                                    Caught: caught,
                                },
                            }),
                        ),
                    ),
                );
            }),
        ),
    );
    RemoveCommission$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CommissionActionTypes.DeleteCommission),
            withLatestFrom(this.store$.select(getToken)),
            concatMap(([{ Payload }, token]) => {
                const queryString = `
mutation{
  deleteCommission(id: ${Payload})
}`;
                return this.gatewayHttpService.graphQl({ query: queryString }, { token }).pipe(
                    withLatestFrom(this.store$.select(getCommissions), this.store$.select(getUserToCommissionFetched), this.store$.select(getUserToCommissionShiftFetched)),
                    concatMap(([res, commissions, userToCommission, userToCommissionShift]) => {
                        const ret: Action[] =
                            res && res.deleteCommission
                                ? [
                                      CommissionActionTypes.UpdateMany({
                                          Payload: [commissions.find((val) => val.Id === Payload).Clone({ Deleted: true })],
                                      }),
                                  ]
                                : [
                                      BaseActionTypes.ErrorAction({
                                          Payload: { ToasterMessage: ErrorCodes.Remove },
                                      }),
                                  ];
                        if (userToCommission) {
                            ret.push(UserToCommissionActionTypes.RemoveByCommission({ Payload }));
                        }
                        if (userToCommissionShift) {
                            ret.push(UserToCommissionShiftActionTypes.RemoveByCommission({ Payload }));
                        }
                        return ret;
                    }),
                    catchError((err, caught) =>
                        of(
                            BaseActionTypes.ErrorAction({
                                Payload: {
                                    ToasterMessage: ErrorCodes.Remove,
                                    Err: err,
                                    Caught: caught,
                                },
                            }),
                        ),
                    ),
                );
            }),
        ),
    );

    ModifyCommission$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CommissionActionTypes.ModifyCommission),
            withLatestFrom(this.store$.select(getToken)),
            concatMap(([action, token]) => {
                const queryString = `
mutation{
  changeCommission(id: ${action.Payload.id}
    ${stringifyIfNotNullOrUndefined(action.Payload, 'additionalData')}
    ${stringifyIfNotNullOrUndefined(action.Payload, 'setAdditionalData')}
    ${stringifyIfNotNullOrUndefined(action.Payload, 'commissionTypeId')}
    ${stringifyIfNotNullOrUndefined(action.Payload, 'personId')}
  ${stringifyIfNotUndefined(action.Payload, 'schadensdatum')}
  ${stringifyIfNotNullOrUndefined(action.Payload, 'versicherungsbedingung')}
  ${stringifyIfNotNullOrUndefined(action.Payload, 'description')}
  ${stringifyIfNotNullOrUndefined(action.Payload, 'interneNummer')}
  ${stringifyIfNotNullOrUndefined(action.Payload, 'auftragsnummer')}
  ${stringifyIfNotNullOrUndefined(action.Payload, 'schadennummer')}
  ${stringifyIfNotNullOrUndefined(action.Payload, 'versicherungsnummer')}
  ${stringifyIfNotUndefined(action.Payload, 'auftragseingangDurchAuftraggeber')}
  ${stringifyIfNotUndefined(action.Payload, 'abgabeterminAG')}
  ${stringifyIfNotUndefined(action.Payload, 'abgabeterminQM')}
  ${stringifyIfNotNullOrUndefined(action.Payload, 'statusFromSVId')}
  ${stringifyIfNotNullOrUndefined(action.Payload, 'statusBackofficeId')}
  ${stringifyIfNotNullOrUndefined(action.Payload, 'ersterBerichtErstellt')}
  ${stringifyIfNotUndefined(action.Payload, 'lastDeadline')}
  ${stringifyIfNotNullOrUndefined(action.Payload, 'customerId')}
  ${stringifyIfNotNullOrUndefined(action.Payload, 'employeeId')}
  ${stringifyIfNotNullOrUndefined(action.Payload, 'aktenzeichen')}
  ${stringifyIfNotNullOrUndefined(action.Payload, 'versicherungsName')}
  ${stringifyIfNotNullOrUndefined(action.Payload, 'versicherungsPhoneNumber')}
  ${stringifyIfNotNullOrUndefined(action.Payload, 'versicherungsMobileNumber')}
  ${stringifyIfNotNullOrUndefined(action.Payload, 'versicherungsCity')}
  ${stringifyIfNotNullOrUndefined(action.Payload, 'versicherungsDescription')}
  ${stringifyIfNotNullOrUndefined(action.Payload, 'versicherungsArbeitsgebiet')}
  ${stringifyIfNotNullOrUndefined(action.Payload, 'versicherungsPostalCode')}
  ${stringifyIfNotNullOrUndefined(action.Payload, 'versicherungsStreet')}
  ${stringifyIfNotNullOrUndefined(action.Payload, 'versicherungsZusatzName')}
  ${stringifyIfNotNullOrUndefined(action.Payload, 'automaticAuftragsnummer')}
  ${stringifyIfNotNullOrUndefined(action.Payload, 'automaticInterneNummer')}
  ${stringifyIfNotNullOrUndefined(action.Payload, 'automaticAccountsReceivableLedger')}
  ${stringifyIfNotNullOrUndefined(action.Payload, 'automaticAccountsReceivableLedgerByClockIns')}
  ${stringifyIfNotUndefined(action.Payload, 'startDate')}
  ${stringifyIfNotUndefined(action.Payload, 'plannedStartDate')}
  ${stringifyIfNotUndefined(action.Payload, 'endDate')}
  ${stringifyIfNotUndefined(action.Payload, 'plannedEndDate')}
  ${stringifyIfNotNullOrUndefined(action.Payload, 'partnerOfficeId')}
  ${stringifyIfNotNullOrUndefined(action.Payload, 'city')}
  ${stringifyIfNotNullOrUndefined(action.Payload, 'postalCode')}
  ${stringifyIfNotNullOrUndefined(action.Payload, 'street')}
  ${stringifyIfNotNullOrUndefined(action.Payload, 'country')}
  ${stringifyIfNotNullOrUndefined(action.Payload, 'versicherungssumme')}
  ${stringifyIfNotNullOrUndefined(action.Payload, 'freeTextField1')}
  ${stringifyIfNotNullOrUndefined(action.Payload, 'freeTextField2')}
  ${stringifyIfNotNullOrUndefined(action.Payload, 'freeTextField3')}
  ${stringifyIfNotNullOrUndefined(action.Payload, 'orderNoCustomer')}
  ${stringifyIfNotNullOrUndefined(action.Payload, 'completeBusinessVolume')}
  ${stringifyIfNotNullOrUndefined(action.Payload, 'costsCompleteBusinessVolume')}
  ${stringifyIfNotUndefined(action.Payload, 'parentId')}
  ${stringifyIfNotUndefined(action.Payload, 'planIds')}
  ${stringifyIfNotUndefined(action.Payload, 'costCenter')}
  ${stringifyIfNotUndefined(action.Payload, 'handoverDate')}
  ${stringifyIfNotUndefined(action.Payload, 'travelZoneId')}
  ${stringifyIfNotUndefined(action.Payload, 'retentionPercentage')}
  ${stringifyIfNotUndefined(action.Payload, 'warrantyRetentionPercentage')}
  ${stringifyIfNotUndefined(action.Payload, 'partialInvoiceDiscountPeriod')}
  ${stringifyIfNotUndefined(action.Payload, 'partialInvoiceReviewPeriod')}
  ${stringifyIfNotUndefined(action.Payload, 'closingInvoiceDiscountPeriod')}
  ${stringifyIfNotUndefined(action.Payload, 'closingInvoiceReviewPeriod')}
  ${stringifyIfNotUndefined(action.Payload, 'closingInvoiceDiscountPercentage')}
  ${stringifyIfNotUndefined(action.Payload, 'partialInvoiceDiscountPercentage')}
  ${stringifyIfNotUndefined(action.Payload, 'closingInvoiceDuePeriod')}
  ${stringifyIfNotUndefined(action.Payload, 'partialInvoiceDuePeriod')}
  ${stringifyIfNotUndefined(action.Payload, 'servicePeriod')}
  ${stringifyIfNotUndefined(action.Payload, 'serviceDate')}
  ${stringifyIfNotUndefined(action.Payload, 'maintenanceFee')}
  ${action.Payload.color ? `color: {
      ${stringifyIfNotUndefined(action.Payload.color, 'red')}
      ${stringifyIfNotUndefined(action.Payload.color, 'green')}
      ${stringifyIfNotUndefined(action.Payload.color, 'blue')}
      ${stringifyIfNotUndefined(action.Payload.color, 'opacity')}
  }` : stringifyIfNotUndefined(action.Payload, 'color') }
  ${stringifyIfNotUndefined(action.Payload, 'offerSubmissionDate')}
  ${stringifyIfNotUndefined(action.Payload, 'negotiationAt')}
  ${stringifyIfNotUndefined(action.Payload, 'departmentId')}
  ${stringifyIfNotUndefined(action.Payload, 'customerSignatureDocumentId')}
  ${action.Payload.kostenstellenart ? `kostenstellenart: ${action.Payload.kostenstellenart}` : ''}
    ){${CommissionEntitygqlFields}}
}`;
                return this.gatewayHttpService.graphQl({ query: queryString }, { token }).pipe(
                    withLatestFrom(
                        this.store$.select(getCommissions),
                        this.store$.select(getStatusFromBackoffice),
                        getMySharepointToken(this.store$)),
                    tap(([res, commissions, statusFromBackoffices, mySharepointToken]) => {
                        if (res?.changeCommission && res.changeCommission.statusBackofficeId !== commissions.find((l) => l.Id === res.changeCommission.id)?.StatusBackofficeId) {
                            this.apiToasterService.success(`Status auf ${statusFromBackoffices?.find((sb) => res.changeCommission.statusBackofficeId === sb.Id).Name} geändert`);
                            if (mySharepointToken) {
                                // poll commission
                                setTimeout(() => this.commissionResolver.pollUpdated(), 200);
                                setTimeout(() => this.commissionResolver.pollUpdated(), 1000);
                                setTimeout(() => this.commissionResolver.pollUpdated(), 5000);
                            }
                        }
                    }),
                    withLatestFrom(this.store$.select(getTokenPayload)),
                    map(([[res, commissions, statusFromBackoffices], tokenpayload]) =>
                        res && res.changeCommission && res.changeCommission.id
                            ? CommissionActionTypes.UpdateMany({
                                  Payload: [CommissionEntityFromBackend(res.changeCommission, tokenpayload?.PartnerId === KAPL_PARTNERID)],
                              })
                            : BaseActionTypes.ErrorAction({
                                  Payload: { ToasterMessage: ErrorCodes.Modify },
                              }),
                    ),
                    catchError((err, caught) =>
                        of(
                            BaseActionTypes.ErrorAction({
                                Payload: {
                                    ToasterMessage: ErrorCodes.Modify,
                                    Err: err,
                                    Caught: caught,
                                },
                            }),
                        ),
                    ),
                );
            }),
        ),
    );

    AddCommission$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CommissionActionTypes.AddCommission),
            withLatestFrom(this.store$.select(getToken), this.store$.select(getPartnerMainOffice)),
            concatMap(([action, token, mainOffice]) => {
                const queryString =
                    `
mutation{
  createCommission` +
                    (isNotNullOrUndefined(action.Payload.customerId) || action.Payload.partnerOfficeId || mainOffice
                        ? `(
    ${stringifyIfNotNullOrUndefined(action.Payload, 'customerId')}
    ${stringifyIfNotNullOrUndefined(action.Payload, 'commissionTypeId')}
    ${stringifyIfNotNullOrUndefined(action.Payload, 'description')}
    ${stringifyIfNotNullOrUndefined(action.Payload, 'additionalData')}
    ${stringifyIfNotNullOrUndefined(action.Payload, 'statusBackofficeId')}
    ${stringifyIfNotNullOrUndefined(action.Payload, 'auftragseingangDurchAuftraggeber')}
    ${stringifyIfNotNullOrUndefined(action.Payload, 'parentId')}
    ${stringifyIfNotNullOrUndefined(action.Payload, 'personId')}
    ${stringifyIfNotNullOrUndefined(action.Payload, 'planIds')}
    ${stringifyIfNotNullOrUndefined(action.Payload, 'auftragsnummer')}
    ${stringifyIfNotNullOrUndefined(action.Payload, 'automaticAuftragsnummer')}
    ${stringifyIfNotNullOrUndefined(action.Payload, 'interneNummer')}
    ${stringifyIfNotNullOrUndefined(action.Payload, 'automaticInterneNummer')}
    ${stringifyIfNotNullOrUndefined(action.Payload, 'addResourcesFromLedgerImportId')}
    ${stringifyIfNotNullOrUndefined(action.Payload, 'handoverDate')}
    ${stringifyIfNotUndefined(action.Payload, 'travelZoneId')}
    ${!action.Payload.partnerOfficeId && mainOffice ? `partnerOfficeId: ${mainOffice.Id}` : stringifyIfNotNullOrUndefined(action.Payload, 'partnerOfficeId')}
    ${stringifyIfNotUndefined(action.Payload, 'retentionPercentage')}
    ${stringifyIfNotUndefined(action.Payload, 'warrantyRetentionPercentage')}
    ${stringifyIfNotUndefined(action.Payload, 'partialInvoiceDiscountPeriod')}
    ${stringifyIfNotUndefined(action.Payload, 'partialInvoiceReviewPeriod')}
    ${stringifyIfNotUndefined(action.Payload, 'closingInvoiceDiscountPeriod')}
    ${stringifyIfNotUndefined(action.Payload, 'closingInvoiceReviewPeriod')}
    ${stringifyIfNotUndefined(action.Payload, 'closingInvoiceDiscountPercentage')}
    ${stringifyIfNotUndefined(action.Payload, 'partialInvoiceDiscountPercentage')}
    ${stringifyIfNotUndefined(action.Payload, 'closingInvoiceDuePeriod')}
    ${stringifyIfNotUndefined(action.Payload, 'partialInvoiceDuePeriod')}
    ${stringifyIfNotUndefined(action.Payload, 'servicePeriod')}
  ${stringifyIfNotUndefined(action.Payload, 'serviceDate')}
  ${stringifyIfNotUndefined(action.Payload, 'maintenanceFee')}
    ${stringifyIfNotUndefined(action.Payload, 'offerSubmissionDate')}
  ${stringifyIfNotUndefined(action.Payload, 'negotiationAt')}
  ${stringifyIfNotUndefined(action.Payload, 'departmentId')}
  ${stringifyIfNotUndefined(action.Payload, 'freeTextField1')}
  ${stringifyIfNotUndefined(action.Payload, 'street')}
  ${stringifyIfNotUndefined(action.Payload, 'city')}
  ${stringifyIfNotUndefined(action.Payload, 'postalCode')}
  ${stringifyIfNotUndefined(action.Payload, 'country')}
  ${stringifyIfNotUndefined(action.Payload, 'kostenstellenart')}
  )`
                        : `${stringifyIfNotNullOrUndefined(action.Payload, 'auftragseingangDurchAuftraggeber')}
                    ${stringifyIfNotNullOrUndefined(action.Payload, 'statusBackofficeId')}`) +
                    `{${CommissionEntitygqlFields}}}`;



                return this.gatewayHttpService.graphQl({ query: queryString }, { token: token }).pipe(
                    withLatestFrom(this.store$.select(getCommissions), this.store$.select(getTokenPayload),getMySharepointToken(this.store$)),
                    concatMap(([res, commissions, tokenpayload, mySharepointToken]) => {
                        if(res?.createCommission && mySharepointToken){
                            setTimeout(() => this.commissionResolver.pollUpdated(), 200);
                            setTimeout(() => this.commissionResolver.pollUpdated(), 1000);
                            setTimeout(() => this.commissionResolver.pollUpdated(), 5000);
                        }
                        return res && res.createCommission
                            ? [
                                  CommissionActionTypes.UpdateOne({
                                      Payload: CommissionEntityFromBackend(res.createCommission, tokenpayload?.PartnerId === KAPL_PARTNERID),
                                  }),
                                  DamageFlowActionTypes.Load({
                                      Payload: {
                                          commissionId: res.createCommission.id,
                                      },
                                  }),
                                  // UserToCommissionActionTypes.SetByCommission({
                                  //     CommissionId: res.createCommission.id,
                                  //     Payload: res.createCommission.user2commission.map(UserToCommissionEntityFromBackend),
                                  // }),
                                  UserToCommissionActionTypes.Load({}),
                              ]
                            : [
                                  BaseActionTypes.ErrorAction({
                                      Payload: { ToasterMessage: ErrorCodes.Add },
                                  }),
                              ];
                    }),
                    catchError((err, caught) =>
                        of(
                            BaseActionTypes.ErrorAction({
                                Payload: {
                                    ToasterMessage: ErrorCodes.Add,
                                    Err: err,
                                    Caught: caught,
                                },
                            }),
                        ),
                    ),
                );
            }),
        ),
    );

    LoadCommissions = createEffect(() =>
        this.actions$.pipe(
            ofType(CommissionActionTypes.Load),
            withLatestFrom(this.store$.select(getTokenPayload)),
            exhaustMap(([{ Payload }, tokenpayload]) =>
                from(this.getCommissionRequest(Payload?.updatedSince, tokenpayload?.PartnerId === KAPL_PARTNERID)).pipe(
                    mergeMap((commissions) => {
                        if (Payload?.updatedSince && Array.isArray(commissions) && commissions.length === 0) {
                            return EMPTY;
                        }

                        return of(
                            Payload?.updatedSince
                                ? CommissionActionTypes.UpdateMany({ Payload: commissions, updateLatestUpdatedAt: true })
                                : CommissionActionTypes.UpdateAll({ Payload: commissions, updateLatestUpdatedAt: true })
                        );
                    }),
                    catchError((err, caught) =>
                        of(
                            BaseActionTypes.ErrorAction({
                                Payload: { ToasterMessage: ErrorCodes.Load, Err: err, Caught: caught },
                            }),
                        ),
                    ),
                ),
            ),
        ),
    );

    private async getCommissionRequest(updatedSince: string | null, commissionDisplayNameCostCenter: boolean) {
        const requestPayload: GetCommissionsRequest = {
            UpdatedSince: updatedSince ? GetTimestampFromTime(new Date(updatedSince)).toString() : undefined,
            WithDeleted: false,
            Ids: undefined,
        };

        const requests$ = new Promise<{
            CommissionCommissionsGet: Partial<GatewayResponse['CommissionCommissionsGet']>;
            Error: GatewayResponse['Errors'];
        }>(async (resolve, reject) => {
            let offsetId: number | null = null;
            const results: GatewayResponse['CommissionCommissionsGet']['Commissions'] = [];
            let errors = {};
            let lastPagedReached = false;

            const getPage = async (offsetId: number | null) => {
                if (offsetId) {
                    requestPayload.OffsetId = offsetId.toString();
                }
                const res = await this.appGatewayService.Request({
                    CommissionCommissionsGet: requestPayload,
                });

                const commissions = res?.CommissionCommissionsGet?.Commissions || [];
                if (commissions.length > 0) {
                    results.push(...commissions);
                }
                const lastId = res?.CommissionCommissionsGet?.LastId;
                offsetId = lastId ? +lastId : null;

                lastPagedReached = !!res?.CommissionCommissionsGet?.LastPage;

                if (res?.Errors && Object.keys(res.Errors).length) {
                    throw res.Errors;
                }

                return offsetId;
            };

            while (!lastPagedReached) {
                try {
                    offsetId = await getPage(offsetId);
                } catch (err) {
                    return reject(err);
                }
            }

            resolve({
                CommissionCommissionsGet: {
                    Commissions: results,
                },
                Error: errors,
            });
        });

        const requestsResponse = await requests$;

        if (Object.keys(requestsResponse?.Error || {}).length === 0) {
            return requestsResponse.CommissionCommissionsGet.Commissions.map((c) => CommissionEntityFromGRPCBackend(c, commissionDisplayNameCostCenter));
        } else {
            throw requestsResponse.Error;
        }
    }

    SetUser$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CommissionActionTypes.SetCommissionUser),
            withLatestFrom(this.store$.select(getToken)),
            concatMap(([action, token]) => {
                const queryString = `mutation{
              setUser2Commission(
                userIds: ${JSON.stringify(action.Payload.UserIds)},
                assigneeUserIds: ${JSON.stringify(action.Payload.AssigneeUserIds)},
                commissionId: ${action.Payload.CommissionId}
              ){
                ${UserToCommissionEntity.GqlFields}
              }
            }`;

                return this.gatewayHttpService.graphQl({ query: queryString }, { token }).pipe(
                    withLatestFrom(
                        this.store$.select(getCommissionById({ id: action.Payload.CommissionId })),
                        this.store$.select(getUserToCommissionFetched),
                        this.store$.select(getUserToCommissionLatestUpdatedAt),
                        this.store$.select(getUser),
                    ),
                    concatMap(([res, commission, u2cFetched, u2cUpdatedAt, user]) => {
                        const ret: Action[] =
                            res && res.setUser2Commission
                                ? [
                                      CommissionActionTypes.UpdateMany({
                                          Payload: [
                                              commission.Clone({
                                                  ImAssigned: res.setUser2Commission.some((u2c) => u2c.userId === user.Id && u2c.assignee),
                                              }),
                                          ],
                                      }),
                                  ]
                                : [BaseActionTypes.ErrorAction({ Payload: { ToasterMessage: ErrorCodes.SetUser } })];
                        if (res && res.setUser2Commission && u2cFetched) {
                            ret.push(
                                // UserToCommissionActionTypes.SetByCommission({
                                //     CommissionId: action.Payload.CommissionId,
                                //     Payload: res.setUser2Commission.map(UserToCommissionEntityFromBackend),
                                // }),
                                UserToCommissionActionTypes.Load({ Payload: { updatedSince: moment(u2cUpdatedAt).toISOString() } }),
                            );
                        }
                        return ret;
                    }),
                    catchError((err, caught) =>
                        of(
                            BaseActionTypes.ErrorAction({
                                Payload: {
                                    ToasterMessage: ErrorCodes.SetUser,
                                    Err: err,
                                    Caught: caught,
                                },
                            }),
                        ),
                    ),
                );
            }),
        ),
    );

    constructor(private actions$: Actions<DaveActions>, private store$: Store<State>, private gatewayHttpService: HttpService, private apiToasterService: ToastrService, private appGatewayService: AppGatewayService, private commissionResolver: CommissionResolver) {}
}
